import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, File as FileIcon, FileImage, FileSpreadsheet, FileText, Folder, FolderOpen, Link2, Mail, Trash2 } from 'lucide-react';
import type { EvidenceRole } from '../../../request/evidence-library/evidenceLibraryRequests.ts';
import { allFolderPaths, buildPathTree, foldersToDepth, type PathNode } from '../pathTree.ts';
import { ROLE_LABELS, ROLE_PILL } from '../labels.ts';

/** One control_evidence row as GET /api/sox-controls/:id/evidence returns it. */
export type ControlEvidenceRow = {
  id: number;
  control_upload_id: number;
  filename: string;
  relative_path?: string | null;
  file_category?: EvidenceRole | null;
  step_mapping?: unknown;
  testing_period_start?: string | null;
  testing_period_end?: string | null;
  uploaded_at?: string | null;
};

export type EvidenceRunUse = {
  run_id: string;
  run_number: number;
  run_name: string;
  workflow_status: string;
  control_id: string;
  role: 'evidence' | 'population' | 'ipe_proof';
};

export type EvidenceUsage = {
  runs: EvidenceRunUse[];
  latest_mapping: { run_number: number; samples: string[]; attributes: string[] } | null;
  other_controls: { control_uuid: string; control_id: string; title: string; file_category: string }[];
};

function fileIcon(name: string) {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  if (['xlsx', 'xls', 'xlsm', 'csv', 'tsv'].includes(ext)) return <FileSpreadsheet className="h-4 w-4 shrink-0 text-green-600" />;
  if (ext === 'pdf') return <FileText className="h-4 w-4 shrink-0 text-red-600" />;
  if (['doc', 'docx'].includes(ext)) return <FileText className="h-4 w-4 shrink-0 text-blue-600" />;
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'tif', 'tiff', 'bmp'].includes(ext)) return <FileImage className="h-4 w-4 shrink-0 text-purple-600" />;
  if (['eml', 'msg'].includes(ext)) return <Mail className="h-4 w-4 shrink-0 text-sky-600" />;
  return <FileIcon className="h-4 w-4 shrink-0 text-gray-500" />;
}

/** Samples and attributes the control-level mapping (step_mapping) names, if any. */
function storedMapping(m: unknown): { samples: string[]; steps: string[] } {
  let v: any = m;
  try {
    if (typeof v === 'string') v = JSON.parse(v);
  } catch {
    return { samples: [], steps: [] };
  }
  if (Array.isArray(v)) return { samples: [], steps: v.map(String) };
  return {
    samples: Array.isArray(v?.samples) ? v.samples.map(String) : [],
    steps: Array.isArray(v?.steps) ? v.steps.map(String) : [],
  };
}

const STATUS_LABEL: Record<string, string> = {
  completed: 'Completed',
  signed_off: 'Signed off',
  in_review: 'In review',
  prepared: 'Prepared',
  failed: 'Failed',
};

function MappingCell({ ev, usage }: { ev: ControlEvidenceRow; usage?: EvidenceUsage }) {
  const role = ev.file_category || 'evidence';
  if (role !== 'evidence') {
    return <span className="text-xs text-gray-400">{role === 'supporting' ? 'Not tested' : 'Used for sampling'}</span>;
  }
  const latest = usage?.latest_mapping;
  if (latest && (latest.samples.length || latest.attributes.length)) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-700" title={`Samples: ${latest.samples.join(', ')}${latest.attributes.length ? `\nAttributes: ${latest.attributes.join(', ')}` : ''}`}>
        <Link2 className="h-3.5 w-3.5" />
        {latest.samples.length} sample{latest.samples.length !== 1 ? 's' : ''}
        {latest.attributes.length ? ` · ${latest.attributes.length} attr${latest.attributes.length !== 1 ? 's' : ''}` : ''}
        <span className="text-gray-400">(RUN-{latest.run_number})</span>
      </span>
    );
  }
  const stored = storedMapping(ev.step_mapping);
  if (stored.samples.length || stored.steps.length) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-700" title={`Samples: ${stored.samples.join(', ')}`}>
        <Link2 className="h-3.5 w-3.5" />
        {stored.samples.length} sample{stored.samples.length !== 1 ? 's' : ''}{stored.steps.length ? ` · ${stored.steps.length} attrs` : ''}
      </span>
    );
  }
  return <span className="text-xs text-gray-400">Unmapped</span>;
}

function UsedInCell({ usage, controlCode, legacy }: { usage?: EvidenceUsage; controlCode: string; legacy: string[] }) {
  const runs = usage?.runs ?? [];
  const others = usage?.other_controls ?? [];
  if (!runs.length && !others.length && !legacy.length) {
    return <span className="text-xs italic text-gray-400">Not used yet</span>;
  }
  // A file used twice in one run (e.g. population and evidence) counts once. The API lists runs newest first.
  const seen = new Set<string>();
  const unique = runs.filter((r) => (seen.has(r.run_id) ? false : (seen.add(r.run_id), true)));
  const mine = unique.filter((r) => !r.control_id || r.control_id === controlCode);
  // Other controls: one chip each, with their runs on hover, so a file shared with a busy control stays one line.
  const byControl = new Map<string, { title: string; runs: EvidenceRunUse[] }>();
  unique.filter((r) => r.control_id && r.control_id !== controlCode).forEach((r) => {
    const e = byControl.get(r.control_id) ?? { title: '', runs: [] };
    e.runs.push(r);
    byControl.set(r.control_id, e);
  });
  others.forEach((o) => {
    const e = byControl.get(o.control_id) ?? { title: '', runs: [] };
    e.title = o.title;
    byControl.set(o.control_id, e);
  });
  const shown = mine.slice(0, 3);
  const runTitle = (r: EvidenceRunUse) =>
    [`RUN-${r.run_number}`, r.run_name, STATUS_LABEL[r.workflow_status] || r.workflow_status, r.role !== 'evidence' ? `as ${ROLE_LABELS[r.role]}` : '']
      .filter(Boolean).join(' · ');
  return (
    <div className="flex flex-wrap items-center gap-1">
      {shown.map((r) => (
        <span key={r.run_id} className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700" title={runTitle(r)}>
          RUN-{r.run_number}
        </span>
      ))}
      {mine.length > shown.length ? (
        <span className="text-xs text-gray-500" title={mine.slice(shown.length).map(runTitle).join('\n')}>+{mine.length - shown.length}</span>
      ) : null}
      {legacy.map((l) => (
        <span key={l} className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600" title="Classic test run">{l}</span>
      ))}
      {Array.from(byControl.entries()).map(([code, e]) => (
        <span
          key={code}
          className="inline-flex items-center whitespace-nowrap rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-800"
          title={[e.title, ...e.runs.map(runTitle)].filter(Boolean).join('\n')}
        >
          {code}{e.runs.length ? ` · ${e.runs.length} run${e.runs.length !== 1 ? 's' : ''}` : ''}
        </span>
      ))}
    </div>
  );
}

/**
 * The control's evidence, laid out in the folders it was uploaded with. Each file shows its role, period, what the
 * latest run mapped it to, and where it is used (runs of this control, runs of other controls, other controls).
 */
export function EvidenceFolderTable({
  files,
  usage,
  controlCode,
  legacyUsage,
  searching,
  formatPeriod,
  onPreview,
  onRemove,
  selected,
  onSelect,
}: {
  files: ControlEvidenceRow[];
  usage: Record<string, EvidenceUsage>;
  controlCode: string;
  /** Classic test executions that analysed a file, by file name. */
  legacyUsage: Record<string, string[]>;
  /** While searching, every folder is open. */
  searching: boolean;
  formatPeriod: (start?: string | null, end?: string | null) => string;
  onPreview: (ev: ControlEvidenceRow) => void;
  /** Remove one file, or every file in a folder (with the folder's name). */
  onRemove: (rows: ControlEvidenceRow[], folderName?: string) => void;
  /** Selected evidence ids, for removing many at once. */
  selected: Set<number>;
  onSelect: (ids: number[], select: boolean) => void;
}) {
  const tree = useMemo(() => buildPathTree(files, (f) => f.relative_path), [files]);
  const [open, setOpen] = useState<Set<string>>(() => new Set(foldersToDepth(tree, 2)));
  // Folders seen before keep the open/closed state the user gave them; only folders new to the list (after an
  // upload) open, to the same depth as at first load.
  const seenFolders = useRef<Set<string>>(new Set(allFolderPaths(tree)));
  useEffect(() => {
    const fresh = foldersToDepth(tree, 2).filter((p) => !seenFolders.current.has(p));
    allFolderPaths(tree).forEach((p) => seenFolders.current.add(p));
    if (fresh.length) setOpen((prev) => new Set([...Array.from(prev), ...fresh]));
  }, [tree]);
  const openPaths = searching ? new Set(allFolderPaths(tree)) : open;

  const toggle = (path: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });

  const rowsUnder = (node: PathNode<ControlEvidenceRow>): ControlEvidenceRow[] =>
    [...node.items, ...node.folders.flatMap(rowsUnder)];
  const allIds = files.map((f) => f.id);
  const allPicked = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const somePicked = allIds.some((id) => selected.has(id));

  const rows: React.ReactNode[] = [];
  const pushFile = (ev: ControlEvidenceRow, depth: number) => {
    const role = (ev.file_category || 'evidence') as EvidenceRole;
    rows.push(
      <tr key={`f-${ev.id}`} className={selected.has(ev.id) ? 'bg-blue-50/50' : 'hover:bg-gray-50'}>
        <td className="w-10 pl-5 pr-0 py-2.5">
          <input type="checkbox" aria-label={`Select ${ev.filename}`} checked={selected.has(ev.id)} onChange={() => onSelect([ev.id], !selected.has(ev.id))} />
        </td>
        <td className="px-5 py-2.5">
          <button
            type="button"
            onClick={() => onPreview(ev)}
            className="flex items-center gap-2 text-left hover:underline"
            style={{ paddingLeft: depth * 20 }}
          >
            {fileIcon(ev.filename)}
            <span className="max-w-[320px] truncate text-gray-800" title={ev.relative_path || ev.filename}>{ev.filename}</span>
          </button>
        </td>
        <td className="px-5 py-2.5">
          <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${ROLE_PILL[role] || ROLE_PILL.evidence}`}>{ROLE_LABELS[role] || role}</span>
        </td>
        <td className="px-5 py-2.5 text-xs text-gray-600">{formatPeriod(ev.testing_period_start, ev.testing_period_end)}</td>
        <td className="px-5 py-2.5"><MappingCell ev={ev} usage={usage[String(ev.control_upload_id)]} /></td>
        <td className="px-5 py-2.5 text-xs text-gray-500">{ev.uploaded_at ? new Date(ev.uploaded_at).toLocaleDateString() : '—'}</td>
        <td className="px-5 py-2.5">
          <UsedInCell usage={usage[String(ev.control_upload_id)]} controlCode={controlCode} legacy={legacyUsage[ev.filename] ?? []} />
        </td>
        <td className="px-5 py-2.5 text-right">
          <button type="button" onClick={() => onRemove([ev])} className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-700">
            <Trash2 className="h-3.5 w-3.5" /> Remove
          </button>
        </td>
      </tr>
    );
  };
  const walk = (node: PathNode<ControlEvidenceRow>, depth: number) => {
    node.folders.forEach((folder) => {
      const isOpen = openPaths.has(folder.path);
      const inFolder = rowsUnder(folder);
      const picked = inFolder.filter((r) => selected.has(r.id)).length;
      rows.push(
        <tr key={`d-${folder.path}`} className="bg-gray-50/60 hover:bg-gray-100/70">
          <td className="w-10 pl-5 pr-0 py-2">
            <input
              type="checkbox"
              aria-label={`Select every file in ${folder.name}`}
              checked={picked > 0 && picked === inFolder.length}
              ref={(el) => { if (el) el.indeterminate = picked > 0 && picked < inFolder.length; }}
              onChange={() => onSelect(inFolder.map((r) => r.id), picked !== inFolder.length)}
            />
          </td>
          <td colSpan={6} className="px-5 py-2">
            <button
              type="button"
              onClick={() => toggle(folder.path)}
              aria-expanded={isOpen}
              className="flex items-center gap-1.5 text-left text-sm font-medium text-gray-800"
              style={{ paddingLeft: depth * 20 }}
            >
              {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-gray-500" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-500" />}
              {isOpen ? <FolderOpen className="h-4 w-4 text-amber-500" /> : <Folder className="h-4 w-4 text-amber-500" />}
              {folder.name}
              <span className="ml-1 text-xs font-normal text-gray-400">{folder.count} file{folder.count !== 1 ? 's' : ''}</span>
            </button>
          </td>
          <td className="px-5 py-2 text-right">
            <button
              type="button"
              onClick={() => onRemove(inFolder, folder.name)}
              className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-red-500 hover:text-red-700"
              title={`Remove every file in ${folder.name}`}
            >
              <Trash2 className="h-3.5 w-3.5" /> Remove folder
            </button>
          </td>
        </tr>
      );
      if (isOpen) walk(folder, depth + 1);
    });
    node.items.forEach((ev) => pushFile(ev, depth));
  };
  walk(tree, 0);

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-600">
            <th className="w-10 pl-5 pr-0 py-3 text-left">
              <input
                type="checkbox"
                aria-label="Select every file shown"
                checked={allPicked}
                ref={(el) => { if (el) el.indeterminate = somePicked && !allPicked; }}
                onChange={() => onSelect(allIds, !allPicked)}
              />
            </th>
            <th className="px-5 py-3 text-left">File</th>
            <th className="w-32 px-5 py-3 text-left">Role</th>
            <th className="w-44 px-5 py-3 text-left">Period</th>
            <th className="w-52 px-5 py-3 text-left">Mapping</th>
            <th className="w-28 px-5 py-3 text-left">Uploaded</th>
            <th className="px-5 py-3 text-left">Used in</th>
            <th className="w-32 px-5 py-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">{rows}</tbody>
      </table>
    </div>
  );
}
